import { useState } from 'react';
import { toast } from 'sonner';
import { PencilSimple, X } from '@phosphor-icons/react';
import api from '../lib/api';
import { useStatusOptions } from '../lib/useStatusOptions';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const ACTIONS = [
  { key: 'set_status', label: 'Set ID/Status' },
  { key: 'add_tag', label: 'Add tag' },
  { key: 'remove_tag', label: 'Remove tag' },
];

// Native bulk edit for selected Contact List rows. Status + tags ONLY —
// notes, emails and phones are protected by design.
export default function BulkEditDialog({ open, contactIds, onClose, onApplied }) {
  const { statuses: statusOptions } = useStatusOptions();
  const [action, setAction] = useState('set_status');
  const [statusValue, setStatusValue] = useState('__blank__');
  const [tagValue, setTagValue] = useState('');
  const [applying, setApplying] = useState(false);
  if (!open) return null;

  const count = contactIds.length;
  const value = action === 'set_status'
    ? (statusValue === '__blank__' ? '' : statusValue)
    : tagValue.trim();
  const tagMissing = action !== 'set_status' && !value;

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post('/ai/contacts/bulk-edit', {
        action, value, target: { contact_ids: contactIds },
      });
      toast.success(`✓ Updated ${data.modified_count} contact${data.modified_count !== 1 ? 's' : ''}`);
      onApplied?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk edit failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-edit-dialog-overlay">
      <div className="bg-white border border-[#B0B0B0] shadow-2xl w-full max-w-[420px] rounded-sm" onClick={(e) => e.stopPropagation()} data-testid="bulk-edit-dialog">
        <div className="px-3 py-2 bg-[#0B5394] text-white text-[12px] font-bold flex items-center justify-between">
          <span className="flex items-center gap-1.5"><PencilSimple size={14} weight="fill" /> Bulk Edit — {count} contact{count !== 1 ? 's' : ''}</span>
          <button onClick={onClose} className="p-0.5 hover:bg-white/20 rounded" data-testid="bulk-edit-dialog-close" aria-label="Close">
            <X size={13} weight="bold" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-1.5">
            {ACTIONS.map((a) => (
              <button
                key={a.key}
                onClick={() => setAction(a.key)}
                className={`px-2.5 py-1.5 text-[11px] font-bold border rounded-sm transition-colors ${action === a.key ? 'bg-[#0B5394] text-white border-[#0B5394]' : 'bg-white text-[#333] border-[#B0B0B0] hover:bg-[#F5F5F5]'}`}
                data-testid={`bulk-edit-action-${a.key}`}
              >
                {a.label}
              </button>
            ))}
          </div>
          {action === 'set_status' ? (
            <Select value={statusValue} onValueChange={setStatusValue}>
              <SelectTrigger className="w-full rounded-sm h-9" data-testid="bulk-edit-status-select">
                <SelectValue placeholder="Choose a status" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="__blank__">(blank)</SelectItem>
                {statusOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input
              placeholder={action === 'add_tag' ? 'Tag to add (e.g. vip)' : 'Tag to remove'}
              value={tagValue}
              onChange={(e) => setTagValue(e.target.value)}
              className="rounded-sm h-9"
              data-testid="bulk-edit-tag-input"
            />
          )}
          <p className="text-[10px] text-[#999]">Only status and tags can be bulk-edited — notes, emails and phones are protected by design.</p>
        </div>
        <div className="px-4 py-2.5 border-t border-[#EEE] flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-[11px] border border-[#999] bg-white hover:bg-[#F5F5F5] rounded-sm" data-testid="bulk-edit-dialog-cancel">Cancel</button>
          <button
            onClick={apply}
            disabled={applying || tagMissing}
            className="px-3 py-1.5 text-[11px] font-bold bg-[#0B5394] hover:bg-[#094180] text-white disabled:opacity-60 rounded-sm"
            data-testid="bulk-edit-dialog-apply"
          >
            {applying ? 'Applying…' : `Apply to ${count} contact${count !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
